import axios from 'axios';
import config from '../config/config';
import { jwtDecode } from 'jwt-decode';

const api = axios.create({
  baseURL: `${config.apiUrl}/api`,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 120000, // Збільшено до 120 секунд для завантаження файлів
  maxRedirects: 3 // Обмежуємо кількість редиректів
});

// Додаємо перехоплювач для додавання токену до кожного запиту
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Перевіряємо чи токен не закінчився
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp < currentTime) {
          console.warn('Token expired, removing from storage');
          localStorage.removeItem('token');
          
          // Створюємо подію для logout
          const logoutEvent = new CustomEvent('forceLogout', {
            detail: { reason: 'Token expired' }
          });
          window.dispatchEvent(logoutEvent);
          
          // Не додаємо токен до запиту
          return config;
        }
        
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Error decoding token:', error);
        localStorage.removeItem('token');
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Додаємо перехоплювач для обробки помилок
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalConfig = error.config;
    // Автоматичний повтор (до 2 спроб) для GET запитів при тимчасових збоях (500, 429, 502, 503, 504 або помилках мережі)
    const isRetryable = !error.response || [429, 500, 502, 503, 504].includes(error.response.status);
    const isGet = !originalConfig?.method || originalConfig.method.toUpperCase() === 'GET';

    if (originalConfig && isRetryable && isGet && !originalConfig.__isRetryDisabled) {
      originalConfig.__retryCount = originalConfig.__retryCount || 0;
      if (originalConfig.__retryCount < 3) {
        originalConfig.__retryCount += 1;
        const delay = 250 * Math.pow(1.8, originalConfig.__retryCount) + Math.random() * 100;
        await new Promise((resolve) => setTimeout(resolve, delay));
        return api(originalConfig);
      }
    }

    // Список публічних маршрутів
    const publicRoutes = [
      '/api/users/',
      '/api/recipes/user/',
      '/api/recipes/public',
      '/api/categories',
      '/api/search',
      '/api/social/user/',
      '/api/subscriptions/user/'
    ];

    // Обробка помилки видалення акаунту
    if (error.response?.status === 403 && error.response?.data?.deletionRequested) {
      // Агресивне перенаправлення на GDPR dashboard
      console.log('🚫 Account deletion detected, redirecting to GDPR dashboard');

      // Використовуємо replace щоб користувач не міг повернутися назад
      if (window.location.pathname !== '/uk/gdpr-dashboard') {
        window.location.replace('/uk/gdpr-dashboard');
      }

      return Promise.reject(error);
    }

    // Обробка rate limiting (429 Too Many Requests)
    if (error.response?.status === 429 || error.response?.status === 403) {
      console.warn('Rate limit exceeded for:', error.config?.url);
      
      // Якщо це запит на зображення, позначаємо як неіснуюче
      if (error.config?.url?.includes('/api/images/')) {
        const imagePath = error.config.url.split('/api/images/')[1];
        if (imagePath) {
          markImageAsFailed(imagePath);
        }
      }
      
      // Отримуємо інформацію про блокування з відповіді
      const retryAfter = error.response?.data?.retryAfter || error.response?.headers?.['retry-after'];
      const isBlocked = error.response?.data?.blocked || error.response?.status === 403;
      const serverMessage = error.response?.data?.error;
      
      // Повертаємо помилку з додатковою інформацією
      return Promise.reject({
        ...error,
        isRateLimit: true,
        retryAfter: retryAfter ? parseInt(retryAfter) : null,
        isBlocked: isBlocked,
        message: serverMessage || 'Занадто багато запитів. Спробуйте пізніше.'
      });
    }

    if (error.response?.status === 401) {
      // Перевіряємо, чи URL запиту відповідає публічному маршруту
      const isPublicRoute = publicRoutes.some(route =>
        error.config?.url?.startsWith(route)
      );

      // Для непублічних маршрутів виконуємо logout
      if (!isPublicRoute) {
        console.warn('🔒 Unauthorized request detected, logging out');
        localStorage.removeItem('token');
        
        // Створюємо подію для logout через AuthContext
        const logoutEvent = new CustomEvent('forceLogout', {
          detail: { reason: 'Token expired or invalid' }
        });
        window.dispatchEvent(logoutEvent);

        // Створюємо подію для показу модального вікна авторизації
        const authRequiredEvent = new CustomEvent('authRequired', {
          detail: { messageKey: 'auth.sessionExpired' }
        });
        window.dispatchEvent(authRequiredEvent);
      }
    }
    return Promise.reject(error);
  }
);

// Кеш для неіснуючих зображень (щоб не робити повторні запити)
const failedImages = new Set();
const imageUrlCache = new Map();

// Асинхронна версія для отримання реального URL
export const getImageUrl = async (imagePath, version = null) => {
  // Якщо немає шляху до зображення, отримуємо дефолтну аватарку з S3
  if (!imagePath || typeof imagePath !== 'string') {
    imagePath = 'avatars/default/default.webp';
  }

  // Якщо це зображення вже помічено як неіснуюче, отримуємо дефолтне зображення з S3
  if (failedImages.has(imagePath)) {
    const defaultPath = imagePath.includes('recipe') 
      ? 'recipes/default/default.png'
      : 'avatars/default/default.webp';
    // Рекурсивно викликаємо для дефолтного зображення
    return getImageUrl(defaultPath);
  }

  // Перевіряємо кеш (з урахуванням версії)
  const cacheKey = version ? `${imagePath}?v=${version}` : imagePath;
  if (imageUrlCache.has(cacheKey)) {
    return imageUrlCache.get(cacheKey);
  }

  // Якщо це вже повний URL (для S3 або інших сервісів)
  if (imagePath.startsWith('http')) {
    imageUrlCache.set(cacheKey, imagePath);
    return imagePath;
  }

  // Очищуємо шлях від /uploads/ якщо є
  let cleanPath = imagePath;
  if (imagePath.startsWith('/uploads/')) {
    cleanPath = imagePath.replace('/uploads/', '');
  }
  if (imagePath.startsWith('uploads/')) {
    cleanPath = imagePath.replace('uploads/', '');
  }

  // Формуємо кінцевий URL з версією
  let finalUrl;
  if (cleanPath.startsWith('recipes/')) {
    finalUrl = `${config.apiUrl}/api/images/${cleanPath}`;
  } else if (cleanPath.startsWith('avatars/')) {
    finalUrl = `${config.apiUrl}/api/images/${cleanPath}`;
  } else {
    finalUrl = `${config.apiUrl}/api/images/${cleanPath}`;
  }

  // Додаємо версію для cache busting
  if (version) {
    const versionValue = typeof version === 'string' && version.includes('T') 
      ? new Date(version).getTime() 
      : version;
    finalUrl += `?v=${versionValue}`;
  }

  imageUrlCache.set(cacheKey, finalUrl);
  return finalUrl;
};

// Синхронна версія для зворотної сумісності
export const getFullImageUrl = (imagePath, version = null) => {
  // Якщо немає шляху до зображення, використовуємо дефолтну аватарку
  if (!imagePath || typeof imagePath !== 'string') {
    imagePath = 'avatars/default/default.png';
  }

  // Якщо це зображення вже помічено як неіснуюче, використовуємо дефолтне зображення
  if (failedImages.has(imagePath)) {
    imagePath = imagePath.includes('recipe')
      ? 'recipes/default/default.png'
      : 'avatars/default/default.png';
  }

  // Перевіряємо кеш (з урахуванням версії)
  const cacheKey = version ? `${imagePath}?v=${version}` : imagePath;
  if (imageUrlCache.has(cacheKey)) {
    return imageUrlCache.get(cacheKey);
  }

  // Якщо це вже повний URL (для S3 або інших сервісів)
  if (imagePath.startsWith('http')) {
    imageUrlCache.set(cacheKey, imagePath);
    return imagePath;
  }

  // Очищуємо шлях від /uploads/ якщо є
  let cleanPath = imagePath;
  if (imagePath.startsWith('/uploads/')) {
    cleanPath = imagePath.replace('/uploads/', '');
  }
  if (imagePath.startsWith('uploads/')) {
    cleanPath = imagePath.replace('uploads/', '');
  }

  let finalUrl;
  // Якщо це ключ S3 для рецептів
  if (cleanPath.startsWith('recipes/')) {
    finalUrl = `${config.apiUrl}/api/images/${cleanPath}`;
  }
  // Якщо це ключ S3 або шлях до аватарки
  else if (cleanPath.startsWith('avatars/')) {
    finalUrl = `${config.apiUrl}/api/images/${cleanPath}`;
  }
  // Для інших випадків
  else {
    finalUrl = `${config.apiUrl}/api/images/${cleanPath}`;
  }

  // Додаємо версію для cache busting
  if (version) {
    const versionValue = typeof version === 'string' && version.includes('T') 
      ? new Date(version).getTime() 
      : version;
    finalUrl += `?v=${versionValue}`;
  }

  // Зберігаємо в кеш
  imageUrlCache.set(cacheKey, finalUrl);
  return finalUrl;
};

// Функція для позначення зображення як неіснуючого
export const markImageAsFailed = (imagePath) => {
  failedImages.add(imagePath);
};

// Функція для очищення кешу (якщо потрібно)
export const clearImageCache = () => {
  failedImages.clear();
  imageUrlCache.clear();
  console.log('🧹 Image cache cleared');
};

// Додаємо функції в window для дебагу
if (typeof window !== 'undefined') {
  window.clearImageCache = clearImageCache;
  window.debugImageCache = () => {
    console.log('Failed images:', Array.from(failedImages));
    console.log('Cached URLs:', Array.from(imageUrlCache.entries()));
  };
}

export const fetchRecipes = async (language = 'uk') => {
  const response = await api.get(`/recipes?language=${language}`);
  return response.data;
};

export const fetchRecipeById = async (id) => {
  const response = await api.get(`/recipes/${id}`);
  return response.data;
};

export const login = async (usernameOrEmail, password, recaptchaToken = null, rememberMe = false) => {
  try {
    const loginData = {
      usernameOrEmail,
      password,
      rememberMe
    };

    // Додаємо reCAPTCHA токен якщо надано
    if (recaptchaToken) {
      loginData.recaptchaToken = recaptchaToken;
    }

    const response = await axios.post(`${config.apiUrl}/api/auth/login`, loginData);

    const { token } = response.data;
    localStorage.setItem('token', token);

    // Видаляємо старі дані з localStorage
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userHandle');
    localStorage.removeItem('theme');
    localStorage.removeItem('language');

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const register = async (email, username, firstName, lastName, password) => {
  const language = (typeof localStorage !== 'undefined' && localStorage.getItem('language')) || 'uk';
  const response = await api.post('/auth/register', { email, username, firstName, lastName, password, language });
  return response.data;
};

export const updateUserLanguage = async (language) => {
  const response = await api.patch('/auth/language', { language });
  return response.data;
};

export const fetchIngredientsByRecipeId = async (recipeId) => {
  const response = await api.get(`/recipes/${recipeId}/ingredients`);
  return response.data;
};

export const fetchPreparationStepsByRecipeId = async (recipeId) => {
  const response = await api.get(`/recipes/${recipeId}/preparation-steps`);
  return response.data;
};

export const fetchUserRecipes = async () => {
  try {
    const response = await api.get('/auth/my-recipes');
    return response.data;
  } catch (error) {
    console.error('Error fetching user recipes:', error);
    return [];
  }
};

export const fetchCategories = async (language = 'uk') => {
  const response = await api.get(`/categories?language=${language}`);
  return response.data;
};

export const fetchUserByHandle = async (userHandle) => {
  try {
    const response = await api.get(`/auth/user/${userHandle}`, {
      timeout: 10000 // 10 секунд для профілю користувача
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user by handle:', userHandle, error?.message || error);
    throw error;
  }
};

export const fetchUserPublicRecipes = async (userHandle) => {
  try {
    const response = await axios.get(`${config.apiUrl}/api/recipes/user/${userHandle}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user public recipes:', error);
    return [];
  }
};

export const fetchFollowers = async (userHandle) => {
  const response = await api.get(`/social/user/${userHandle}/followers`);
  return response.data;
};

export const fetchFollowing = async (userHandle) => {
  const response = await api.get(`/social/user/${userHandle}/following`);
  return response.data;
};

export const followUser = async (userHandle) => {
  const response = await api.post(`/social/user/${userHandle}/follow`);
  return response.data;
};

export const unfollowUser = async (userHandle) => {
  const response = await api.delete(`/social/user/${userHandle}/unfollow`);
  return response.data;
};

export const getFollowStatus = async (userHandle) => {
  const response = await api.get(`/social/user/${userHandle}/follow-status`);
  return response.data;
};

// Функції для запитів на підписку
export const sendFollowRequest = async (userHandle, message = '') => {
  try {
    const response = await api.post(`/follow-requests/send/${userHandle}`, { message });
    return response.data;
  } catch (error) {
    console.error('Error sending follow request:', error);
    throw error;
  }
};

export const getIncomingFollowRequests = async (page = 1, limit = 20) => {
  try {
    const response = await api.get(`/follow-requests/incoming?page=${page}&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error getting incoming follow requests:', error);
    throw error;
  }
};

export const getOutgoingFollowRequests = async (page = 1, limit = 20) => {
  try {
    const response = await api.get(`/follow-requests/outgoing?page=${page}&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error getting outgoing follow requests:', error);
    throw error;
  }
};

export const acceptFollowRequest = async (requestId) => {
  if (!requestId || requestId === 'undefined') {
    throw new Error('Invalid request ID');
  }

  try {
    const response = await api.post(`/follow-requests/accept/${requestId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Запит не знайдено або вже оброблено');
    }
    if (error.response?.status === 400) {
      throw new Error('Неправильний запит');
    }
    throw new Error('Помилка при прийнятті запиту');
  }
};

export const rejectFollowRequest = async (requestId) => {
  if (!requestId || requestId === 'undefined') {
    throw new Error('Invalid request ID');
  }

  try {
    const response = await api.post(`/follow-requests/reject/${requestId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Запит не знайдено або вже оброблено');
    }
    if (error.response?.status === 400) {
      throw new Error('Неправильний запит');
    }
    throw new Error('Помилка при відхиленні запиту');
  }
};

export const getBlockedUsers = async () => {
  const response = await api.get('/users/blocked');
  return response.data;
};

export const blockUser = async (userId) => {
  const response = await api.post(`/users/${userId}/block`);
  return response.data;
};

export const unblockUser = async (userId) => {
  const response = await api.delete(`/users/${userId}/block`);
  return response.data;
};

export const cancelFollowRequest = async (userHandle) => {
  try {
    const response = await api.delete(`/follow-requests/cancel/${userHandle}`);
    return response.data;
  } catch (error) {
    console.error('Error canceling follow request:', error);
    throw error;
  }
};

export const getFollowRequestStatus = async (userHandle) => {
  try {
    const response = await api.get(`/follow-requests/status/${userHandle}`);
    return response.data;
  } catch (error) {
    console.error('Error getting follow request status:', error);
    throw error;
  }
};

// Рейтинги
export const addRating = async (recipeId, stars, comment) => {
  const response = await api.post(`/ratings/recipes/${recipeId}/rating`, { stars, comment });
  return response.data;
};

export const getRecipeRatings = async (recipeId) => {
  const { requestCache } = await import('./requestCache');
  const cacheKey = `recipe_ratings_${recipeId}`;
  
  return requestCache.request(cacheKey, async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/ratings/recipes/${recipeId}/ratings`);
      return response.data;
    } catch (error) {
      // Повертаємо дефолтні значення у випадку помилки
      // Silenced to prevent console errors in Lighthouse audits
      return {
        stats: {
          totalRatings: 0,
          averageRating: '0.0'
        },
        recentRatings: []
      };
    }
  });
};

export const getUserRating = async (recipeId) => {
  const response = await api.get(`/ratings/recipes/${recipeId}/user-rating`);
  return response.data;
};

export const saveRecipe = async (recipeId) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Необхідна авторизація');

  const response = await api.post(`/saved/recipes/${recipeId}/save`);
  return response.data;
};

export const getSavedRecipes = async () => {
  const response = await api.get('/saved/saved-recipes');
  return response.data;
};

export const checkSavedStatus = async (recipeId) => {
  const response = await api.get(`/saved/recipes/${recipeId}/saved-status`);
  return response.data;
};

// Додаємо нові функції
export const addToViewHistory = async (recipeId) => {
  try {
    const response = await api.post(`/history/recipes/${recipeId}/view`);
    return response.data;
  } catch (error) {
    console.error('Error adding to view history:', error);
    throw error;
  }
};

export const fetchViewHistory = async (userHandle = null) => {
  const params = userHandle ? `?userHandle=${userHandle}` : '';
  const response = await api.get(`/history/history${params}`);
  return response.data;
};

export const fetchStatistics = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await api.get(`/admin/statistics${queryString ? `?${queryString}` : ''}`);
  return response.data;
};

export const fetchUsers = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await api.get(`/admin/users${queryString ? `?${queryString}` : ''}`);
  return response.data;
};

export const updateUserRole = async (userId, roleId) => {
  const response = await api.patch(`/admin/users/${userId}/role`, { roleId });
  return response.data;
};

export const getUserDetails = async (userId) => {
  const response = await api.get(`/admin/users/${userId}`);
  return response.data;
};

export const getAdminRecipes = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await api.get(`/admin/recipes${queryString ? `?${queryString}` : ''}`);
  return response.data;
};

export const duplicateRecipeForTranslation = async (recipeId, translationData) => {
  const response = await api.post(`/admin/recipes/${recipeId}/duplicate`, translationData);
  return response.data;
};

export const searchAll = async (query) => {
  try {
    const response = await axios.get(`${config.apiUrl}/api/search?q=${encodeURIComponent(query)}`);
    return response.data;
  } catch (error) {
    console.error('Error searching:', error);
    throw error;
  }
};

/**
 * Отримує та декодує дані з JWT токена
 * @returns {Object|null} Декодовані дані токена або null у випадку помилки
 */
export const getTokenData = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('Token not found in localStorage');
      return null;
    }

    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    if (!decoded.userHandle) {
      console.error('User handle not found in token payload');
      return null;
    }

    if (decoded.exp < currentTime) {
      console.warn('Token has expired');
      localStorage.removeItem('token');
      return null;
    }

    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    localStorage.removeItem('token');
    return null;
  }
};

/**
 * Перевіряє автентифікацію користувача
 * @returns {boolean} Статус автентифікації
 */
export const isAuthenticated = () => {
  const tokenData = getTokenData();
  return Boolean(tokenData?.userId && tokenData?.userHandle);
};

export const getUserRole = () => {
  const tokenData = getTokenData();
  return tokenData?.roleId || null;
};

export const logout = () => {
  localStorage.removeItem('token');
};

export const requestPasswordReset = async (email, recaptchaToken = null) => {
  try {
    const requestData = { email };
    
    // Додаємо reCAPTCHA токен якщо надано
    if (recaptchaToken) {
      requestData.recaptchaToken = recaptchaToken;
    }
    
    const response = await api.post('/auth/request-reset', requestData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const verifyResetCode = async (email, code) => {
  try {
    const response = await api.post('/auth/verify-reset', { email, code });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (email, code, newPassword, recaptchaToken = null) => {
  try {
    const requestData = {
      email,
      code,
      newPassword
    };
    
    // Додаємо reCAPTCHA токен якщо надано
    if (recaptchaToken) {
      requestData.recaptchaToken = recaptchaToken;
    }
    
    const response = await api.post('/auth/reset-password', requestData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Google OAuth
export const googleAuth = async (idToken) => {
  try {
    const response = await api.post('/auth/google', {
      idToken
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Facebook OAuth
export const facebookAuth = async (accessToken) => {
  try {
    const response = await api.post('/auth/facebook', {
      accessToken
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// PDF Export functions
const handlePdfResponse = async (response, fallbackFilename) => {
  const contentType = (response.headers && response.headers['content-type']) || '';

  // Великий PDF сервер віддає як JSON з посиланням на S3 (ліміт відповіді API 6 МБ)
  if (contentType.includes('application/json')) {
    let data = response.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      data = JSON.parse(await data.text());
    }
    if (data && data.downloadUrl) {
      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = data.filename || fallbackFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    throw new Error((data && data.message) || 'PDF export failed');
  }

  // Малий PDF приходить напряму як бінарні дані
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fallbackFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const exportRecipeToPDF = async (recipeId, language = 'uk') => {
  try {
    const response = await api.get(`/pdf/recipe/${recipeId}?language=${language}`, {
      responseType: 'blob'
    });

    await handlePdfResponse(response, `recipe_${recipeId}.pdf`);

    return { success: true };
  } catch (error) {
    console.error('Error exporting recipe to PDF:', error);
    throw error;
  }
};

export const exportMultipleRecipesToPDF = async (recipeIds, language = 'uk') => {
  try {
    const response = await api.post(`/pdf/recipes?language=${language}`, {
      recipeIds
    }, {
      responseType: 'blob'
    });

    await handlePdfResponse(
      response,
      `recipe_collection_${new Date().toISOString().split('T')[0]}.pdf`
    );

    return { success: true };
  } catch (error) {
    console.error('Error exporting recipes to PDF:', error);
    throw error;
  }
};

// Notifications API
export const fetchNotifications = async (page = 1, limit = 20) => {
  try {
    const response = await api.get(`/notifications?page=${page}&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await api.patch(`/notifications/${notificationId}/read`);
    return response.data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async () => {
  try {
    const response = await api.patch('/notifications/mark-all-read');
    return response.data;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

export const getUnreadNotificationsCount = async () => {
  try {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  } catch (error) {
    console.error('Error fetching unread notifications count:', error);
    throw error;
  }
};

// Адмін функції для сповіщень
export const sendAdminMessage = async (data) => {
  try {
    const response = await api.post('/notifications/admin/send', data);
    return response.data;
  } catch (error) {
    console.error('Error sending admin message:', error);
    throw error;
  }
};

export const sendBroadcastMessage = async (data) => {
  try {
    const response = await api.post('/notifications/admin/broadcast', data);
    return response.data;
  } catch (error) {
    console.error('Error sending broadcast message:', error);
    throw error;
  }
};

export const sendAdminEmail = async (data) => {
  try {
    const response = await api.post('/notifications/admin/email', data);
    return response.data;
  } catch (error) {
    console.error('Error sending admin email:', error);
    throw error;
  }
};

export const getUsersForAdmin = async (search = '') => {
  try {
    const response = await api.get(`/notifications/admin/users?search=${search}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching users for admin:', error);
    throw error;
  }
};

// Push-сповіщення (адмін)
export const previewPushAudience = async (data) => {
  const response = await api.post('/push/admin/preview', data);
  return response.data;
};

export const sendPushNotification = async (data) => {
  const response = await api.post('/push/admin/send', data);
  return response.data;
};

export const getPushHistory = async (limit = 50) => {
  const response = await api.get(`/push/admin/history?limit=${limit}`);
  return response.data;
};

export const getPushStats = async () => {
  const response = await api.get('/push/admin/stats');
  return response.data;
};

// Функція для отримання підписки користувача (без кешування)
export const getUserSubscription = async (userHandle) => {
  const response = await api.get(`/subscriptions/user/${userHandle}`);
  return response.data;
};


export const getAutoPushScenarios = async () => {
  const response = await api.get('/push/admin/automated');
  return response.data;
};

export const updateAutoPushScenario = async (id, data) => {
  const response = await api.put(`/push/admin/automated/${id}`, data);
  return response.data;
};

export const testAutoPushScenario = async (id) => {
  const response = await api.post(`/push/admin/automated/${id}/test`);
  return response.data;
};

export default api;

